import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {Spin} from 'antd';
import {toast} from 'react-toastify';
import {GET_INVESTOR_DISTRIBUTION_BY_REGION} from '../resolvers';
import InvestorsRegionUsersModal from './InvestorsRegionUsersModal';
import {Card, EmptyState, Section, ShareBar} from '../../shared/DashboardUI';
import DateRangeFilter, {describeRange} from '../../shared/DateRangeFilter';
import {plainNumber, seriesColor} from '../../shared/chartTheme';
import styles from '../../shared/dashboard.module.scss';

/**
 * Where investors and users are.
 *
 * The cards were sorted alphabetically, which puts Central Africa — with no
 * investors at all — above the region carrying most of them. A distribution is
 * read by size, so the list is ordered by investor count and each region shows
 * its share of the total. Regions with nothing sink to the bottom instead of
 * leading the page with two zeros.
 */

const formatRegion = region =>
  (region || '—')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());

const InvestorsRegionsTab = () => {
  const [range, setRange] = useState({startDate: null, endDate: null});
  const [modal, setModal] = useState({
    open: false,
    region: null,
    mode: 'investors',
  });

  const {data, loading} = useQuery(GET_INVESTOR_DISTRIBUTION_BY_REGION, {
    variables: {filters: range},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: () => toast.error('Could not load investor distribution'),
  });

  const result = data?.getInvestorDistributionByRegion;

  useEffect(() => {
    if (result?.__typename === 'Error' && result?.message)
      toast.error(result.message);
  }, [result]);

  const regions = useMemo(() => {
    const list = result?.data?.regions;
    if (!Array.isArray(list)) return [];
    return [...list].sort(
      (a, b) =>
        (Number(b?.investorCount) || 0) - (Number(a?.investorCount) || 0) ||
        (Number(b?.userCount) || 0) - (Number(a?.userCount) || 0),
    );
  }, [result?.data?.regions]);

  const totals = useMemo(
    () =>
      regions.reduce(
        (sums, region) => ({
          investors: sums.investors + (Number(region?.investorCount) || 0),
          users: sums.users + (Number(region?.userCount) || 0),
        }),
        {investors: 0, users: 0},
      ),
    [regions],
  );

  const open = useCallback(
    (region, mode) => region && setModal({open: true, region, mode}),
    [],
  );

  const close = useCallback(
    () => setModal(current => ({...current, open: false})),
    [],
  );

  return (
    <div className={styles.page} style={{padding: 0}}>
      <DateRangeFilter value={range} onChange={setRange} loading={loading} />

      <Section
        title="Investor regions"
        description="Ordered by investor count. Select a figure to list the accounts behind it."
        meta={`${plainNumber(totals.investors)} investors · ${plainNumber(
          totals.users,
        )} users · ${describeRange(range)}`}>
        {loading ? (
          <div className={styles.loading}>
            <Spin />
            <span>Loading regions…</span>
          </div>
        ) : result?.__typename === 'Error' ? (
          <EmptyState
            title="Could not load investor region data"
            description="The request came back with an error. Try a different range or reload."
          />
        ) : regions.length === 0 ? (
          <EmptyState
            title="No investor region data for this period"
            description="Widen the date range to include earlier accounts."
          />
        ) : (
          <div className={styles.regionGrid}>
            {regions.map((region, index) => {
              const investors = Number(region?.investorCount) || 0;
              const users = Number(region?.userCount) || 0;
              return (
                <Card key={region?.region ?? index}>
                  <div className={styles.regionHead}>
                    <h3>{formatRegion(region?.region)}</h3>
                    {totals.investors > 0 && (
                      <span className={styles.sectionMeta}>
                        {Math.round((investors * 100) / totals.investors)}% of
                        investors
                      </span>
                    )}
                  </div>

                  <ShareBar
                    value={investors}
                    total={totals.investors}
                    color={seriesColor(index)}
                  />

                  <div className={styles.regionFigures}>
                    <button
                      type="button"
                      className={styles.regionFigure}
                      disabled={!investors}
                      onClick={() => open(region?.region, 'investors')}>
                      <span className={styles.miniLabel}>Investors</span>
                      <span className={styles.miniValue}>
                        {plainNumber(investors)}
                      </span>
                    </button>
                    <button
                      type="button"
                      className={styles.regionFigure}
                      disabled={!users}
                      onClick={() => open(region?.region, 'users')}>
                      <span className={styles.miniLabel}>Users</span>
                      <span className={styles.miniValue}>
                        {plainNumber(users)}
                      </span>
                    </button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Section>

      <InvestorsRegionUsersModal
        open={modal.open}
        region={modal.region}
        initialListMode={modal.mode}
        appliedOverviewDates={range}
        onClose={close}
      />
    </div>
  );
};

export default InvestorsRegionsTab;
